import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';
import { PHONE_DISPLAY, PHONE_HREF, trackCallConversion } from '../utils/phone';
const navLinks = [{
  to: '/',
  label: 'Home'
}, {
  to: '/services',
  label: 'Services'
}, {
  to: '/about',
  label: 'About'
}, {
  to: '/specials',
  label: 'Specials'
}, {
  to: '/gallery',
  label: 'Gallery'
}, {
  to: '/reviews',
  label: 'Reviews'
}, {
  to: '/contact',
  label: 'Contact'
}];
const serviceSubLinks = [{
  to: '/full-detail',
  label: 'Full Detail'
}, {
  to: '/wash-wax',
  label: 'Wash & Wax'
}, {
  to: '/basic-wash',
  label: 'Basic Wash'
}];
/** Service sub-pages keep the Services item highlighted. */
const servicePaths = ['/services', '/full-detail', '/wash-wax', '/basic-wash'];
export function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const {
    pathname
  } = useLocation();

  // Close the mobile menu whenever the page changes.
  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);
  const isActive = (to: string) => {
    if (to === '/') return pathname === '/';
    if (to === '/services') return servicePaths.some(p => pathname === p || pathname.startsWith(`${p}/`));
    return pathname === to;
  };
  const linkClass = (to: string) => `font-bold uppercase tracking-wider transition-colors duration-150 ${isActive(to) ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-400'}`;
  return <header className="fixed top-0 left-0 right-0 z-50" data-id="element-226" data-mp="10elwjg8lsy9g">
      {/* Promo Bar */}
      <Link to="/specials" style={{
      backgroundColor: "#FFE81A",
      backgroundImage: "none"
    }} className="block w-full text-black text-center py-1.5 px-3 text-xs sm:text-sm md:text-base font-black uppercase tracking-wide leading-snug hover:brightness-110 transition-[filter] duration-150" data-id="element-227" data-mp="1586l6hob4fvz">
        🔥 Limited Time Only!{' '}
        <span className="text-base sm:text-lg md:text-xl" style={{
        color: '#C92121'
      }} data-id="element-228" data-mp="1k4e5dhzpthb9">
          $50 OFF
        </span>{' '}
        Listed Price of Full Details &{' '}
        <span className="text-base sm:text-lg md:text-xl" style={{
        color: '#C92121'
      }} data-id="element-229" data-mp="pi8k9wczj5h1">
          $20 OFF
        </span>{' '}
        Wash &amp; Wax — Call Now!
      </Link>

      <nav className="bg-black/95 backdrop-blur-sm border-b border-zinc-900" aria-label="Main navigation" data-id="element-230" data-mp="zetpoisx2ile">
        <div className="max-w-7xl mx-auto px-4" data-id="element-231" data-mp="1d5gp6d6i4cij">
          <div className="flex items-center justify-between gap-4 h-16" data-id="element-232" data-mp="1d8ba05tffzch">
            <Link to="/" className="text-lg sm:text-xl font-black uppercase tracking-tight whitespace-nowrap" aria-label="Mike's Pro Detail - Home" data-id="element-233" data-mp="186vlg3s1japc">
              <span className="text-white" data-id="element-234" data-mp="1i5uon6u1ee4y">Mike's</span>{' '}
              <span className="text-red-600" data-id="element-235" data-mp="1i6cumv6i46q2">Pro Detail</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-5 xl:gap-7" data-id="element-236" data-mp="3jwxyhtw3roog">
              <ul className="flex items-center gap-5 xl:gap-6" data-id="element-237" data-mp="3jysj173gpes0">
                {navLinks.map(link => <li key={link.to} data-id="element-238" data-mp="1zffe1kzi4ch9">
                    <NavLink to={link.to} end={link.to === '/'} className={`${linkClass(link.to)} text-sm`} aria-current={isActive(link.to) ? 'page' : undefined} data-id="element-239" data-mp="24e0dhi6xis4n">
                      {link.label}
                    </NavLink>
                  </li>)}
              </ul>
              <a href={PHONE_HREF} onClick={trackCallConversion} className="hidden xl:flex flex-col leading-tight text-right text-white hover:text-yellow-400 transition-colors duration-150" aria-label={`Call or text Mike's Pro Detail at ${PHONE_DISPLAY}`} data-id="element-240" data-mp="1umcgz2vimkwb">
                <span className="text-[11px] uppercase tracking-wider text-gray-400 font-bold" data-id="element-241" data-mp="16r1doykxx42j">
                  Call or Text
                </span>
                <span className="text-lg font-black whitespace-nowrap" data-id="element-242" data-mp="1brfatll06tls">
                  {PHONE_DISPLAY}
                </span>
              </a>
              <Link to="/contact" className="px-5 py-2.5 bg-red-600 hover:bg-red-500 text-white font-black text-sm uppercase tracking-wider rounded-sm whitespace-nowrap transition-colors duration-150" data-id="element-243" data-mp="363vnb44e73z3">
                Get a Quote
              </Link>
            </div>

            {/* Mobile controls */}
            <div className="flex lg:hidden items-center gap-1" data-id="element-244" data-mp="wzmvwn2j5vco">
              <a href={PHONE_HREF} onClick={trackCallConversion} className="p-2 text-red-500" aria-label={`Call or text Mike's Pro Detail at ${PHONE_DISPLAY}`} data-id="element-245" data-mp="s1bnmgrvvswk">
                <Phone className="w-6 h-6" data-id="element-246" data-mp="n3tccjqe91le" />
              </a>
              <button type="button" onClick={() => setIsOpen(open => !open)} className="text-white p-2 -mr-2" aria-label={isOpen ? 'Close menu' : 'Open menu'} aria-expanded={isOpen} aria-controls="mobile-menu" data-id="element-247" data-mp="ib06fsj6vn7q">
                {isOpen ? <X className="w-6 h-6" data-id="element-248" data-mp="9bvcz0mhik2f" /> : <Menu className="w-6 h-6" data-id="element-249" data-mp="9coixzdmd5ji" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence data-id="element-250" data-mp="3vhigrtcoa7r3">
          {isOpen && <motion.div id="mobile-menu" initial={{
          opacity: 0,
          y: -8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.18,
          ease: [0.23, 1, 0.32, 1]
        }} className="lg:hidden border-t border-zinc-900 max-h-[calc(100vh-7rem)] overflow-y-auto" data-id="element-251" data-mp="3vik1kpjhq8m0">
              <ul className="max-w-7xl mx-auto px-4 py-4 flex flex-col" data-id="element-252" data-mp="2oyh9io8copnk">
                {navLinks.map(link => <li key={link.to} data-id="element-253" data-mp="1sat4n7xautly">
                    <NavLink to={link.to} end={link.to === '/'} className={`${linkClass(link.to)} block py-3 text-base`} data-id="element-254" data-mp="1s7ow5ycsxbph">
                      {link.label}
                    </NavLink>
                    {link.to === '/services' && <ul className="pl-4 border-l border-zinc-800 mb-2" data-id="element-255" data-mp="260o8xsl78rtc">
                        {serviceSubLinks.map(sub => <li key={sub.to} data-id="element-256" data-mp="19ja375ixbnm1">
                            <NavLink to={sub.to} className={`${linkClass(sub.to)} block py-2 text-sm`} data-id="element-257" data-mp="19iikgvafdkd6">
                              {sub.label}
                            </NavLink>
                          </li>)}
                      </ul>}
                  </li>)}
                <li className="mt-4 grid gap-3" data-id="element-258" data-mp="2bwy85mze6d7c">
                  <Link to="/contact" className="flex items-center justify-center px-5 py-3.5 bg-red-600 hover:bg-red-500 text-white font-black uppercase tracking-wider rounded-sm transition-colors duration-150" data-id="element-259" data-mp="2bw6ckc5sofh2">
                    Get a Quote
                  </Link>
                  <a href={PHONE_HREF} onClick={trackCallConversion} className="flex items-center justify-center gap-2 px-5 py-3.5 border-2 border-white/80 text-white font-black uppercase tracking-wider rounded-sm whitespace-nowrap" data-id="element-260" data-mp="1xxq52j03ykxv">
                    <Phone className="w-4 h-4 shrink-0" aria-hidden="true" data-id="element-261" data-mp="26v8vk2witrh5" />
                    Call or Text {PHONE_DISPLAY}
                  </a>
                </li>
              </ul>
            </motion.div>}
        </AnimatePresence>
      </nav>
    </header>;
}